import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { getCaretPosition, getSelectedText } from '../../../Helpers/Chat/ContentEditableEle';
import { getUserDetails } from '../../../Helpers/Chat/User';
import { CHAT_TYPE_GROUP } from '../../../Helpers/Chat/Constant';
import { setMentionedUserInfo } from '../../../slice/selectedUserSlice';
import { i18n } from '../../../localization/i18n';
import { readComposerText, refuseOverLimit } from '../../../features/chatDetail/Composer/composerCaret';
import { fitPaste } from '../../../features/chatDetail/Composer/composerRules';
import { decorateComposer } from '../../../features/chatDetail/Composer/decorateComposer';
import { continueBlock, exitBlockAtEnd } from '../../../features/chatDetail/Composer/listContinuation';
import { filterMentionMembers, isEveryoneQuery } from '../../../features/chatDetail/Composer/mentionCandidates';

class ContentEditable extends Component {
    constructor(props = {}) {
        super(props);
        this.state = {
            lastHtml: '',
        };
        this.searchView = true;
        this.searchValue = '';
        this.searchPos = 0;
    }

    shouldComponentUpdate(nextProps) {
        return nextProps.html !== this.typingContainner.innerHTML;
    }

    componentDidUpdate() {
        if (this.props.html !== this.typingContainner.innerHTML) {
            this.typingContainner.innerHTML = this.props.html;
        }
    }

    pasteAsPlainText = (event = {}) => {
        event.preventDefault();

        const msgContent = this.typingContainner;
        if (!msgContent) return;

        // FR103: a paste past 10,000 characters is cut silently.
        const text = fitPaste(readComposerText(msgContent), event.clipboardData?.getData('text/plain') || '');
        if (!text) return;

        const selection = window.getSelection();
        if (!selection.rangeCount) return;

        const range = selection.getRangeAt(0);
        range.deleteContents();

        // Instead of inserting <br>, insert a text node that keeps newlines
        const textNode = document.createTextNode(text);
        range.insertNode(textNode);

        // Move caret after inserted text
        range.setStartAfter(textNode);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);

        // optional scroll to bottom
        msgContent.scrollTop = msgContent.scrollHeight;

        this.emitChange();
    };

    emitChange = (event, isBack = false, isShow = false, isClickedTypingField = false) => {
        let html = this.props.decorate
            ? decorateComposer(this.typingContainner, event)
            : this.typingContainner.innerHTML;
        let newStr = html.replace(/<br>/g, '');
        let selection = window.getSelection();
        if (
            (!html && html.replace(/^\s+/, '') === html) ||
            this.typingContainner.innerHTML === '<br>' ||
            selection.toString().length === this.typingContainner.innerHTML.length
        ) {
            this.props.handleMessage({
                target: {
                    value: '',
                },
            });
            this.props.handleEmptyContent();

            // Close mention view when content is empty
            if (this.props.handleMentionView) {
                this.props.handleMentionView(false, []);
            }

            this.setState({ lastHtml: '' });
            return;
        }
        if (this.props.handleMessage) {
            this.props.onInputListener();
            this.currentPosition();
            this.props.handleMessage(
                {
                    target: {
                        value: html,
                    },
                },
                isClickedTypingField,
            );
        }

        this.lastHtml = newStr;
        if (this.typingContainner) {
            this.typingContainner.scrollTop = this.typingContainner.scrollHeight;
        }
        const lastElement = newStr.slice(-1);
        const secondLastElement = newStr.slice(-2, -1);
        const thirdLastElement = newStr.slice(-3, -2);
        const position = getCaretPosition(this.typingContainner);
        let midPosLast = position - 1;
        let midPosSecondLast = newStr.charAt(position - 2);
        let midLastEle = newStr.charAt(position);
        let midSecondLastEle = newStr.charAt(midPosLast);
        if (this.props.chatType === CHAT_TYPE_GROUP) {
            if (newStr.length > 0) {
                this.handleMentionEnableDisable(
                    isBack,
                    lastElement,
                    secondLastElement,
                    thirdLastElement,
                    midLastEle,
                    midSecondLastEle,
                    midPosSecondLast,
                );

                //Handles the search in mention Popup
                const pattern = /\B@\.*/g;
                if (newStr.match(pattern)) {
                    this.handleMentionTextSearch(position, newStr, isBack, midLastEle, midSecondLastEle, isShow);
                }
            }
        }
    };

    // Add this helper method to get plain text excluding mentioned spans
    getPlainTextExcludingMentions = (rootNode = this.typingContainner) => {
        let plainText = '';
        const walker = document.createTreeWalker(
            rootNode,
            NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
            {
                acceptNode: node => {
                    // Skip text inside mentioned spans
                    if (node.nodeType === Node.ELEMENT_NODE && node.classList && node.classList.contains('mentioned')) {
                        return NodeFilter.FILTER_REJECT;
                    }
                    return NodeFilter.FILTER_ACCEPT;
                },
            },
            false,
        );

        let node;
        while ((node = walker.nextNode())) {
            if (node.nodeType === Node.TEXT_NODE) {
                plainText += node.textContent;
            }
        }

        return plainText;
    };

    getTextBeforeCursorExcludingMentions = () => {
        const selection = window.getSelection();
        if (!selection.rangeCount) return '';
        const range = selection.getRangeAt(0);

        // Safety check: ensure the selection is within our container
        if (!this.typingContainner.contains(range.startContainer)) {
            return '';
        }

        const preCaretRange = range.cloneRange();
        preCaretRange.selectNodeContents(this.typingContainner);
        preCaretRange.setEnd(range.endContainer, range.endOffset);

        const tempDiv = document.createElement('div');
        tempDiv.appendChild(preCaretRange.cloneContents());
        return this.getPlainTextExcludingMentions(tempDiv);
    };

    handleMentionTextSearch = (_position, _newStr, _isBack, _midLastEle, _midSecondLastEle, _isShow) => {
        // Get plain text excluding mentioned spans
        const plainText = this.getPlainTextExcludingMentions();
        const textBeforeCursor = this.getTextBeforeCursorExcludingMentions();
        const pos = textBeforeCursor.length;

        // Find the last @ on the CURRENT line only (not entire text)
        // Because we sliced up to cursor, "beforeCursor" IS textBeforeCursor

        // Find the last newline before cursor to get current line start
        const lastNewlineIndex = textBeforeCursor.lastIndexOf('\n');
        const currentLineStart = lastNewlineIndex === -1 ? 0 : lastNewlineIndex + 1;
        const currentLineText = textBeforeCursor.substring(currentLineStart);

        // Find @ on current line only
        const lastAtIndexOnLine = currentLineText.lastIndexOf('@');
        if (lastAtIndexOnLine === -1) return;

        // Calculate absolute position of @ in full text
        const lastAtIndex = currentLineStart + lastAtIndexOnLine;

        // valid if @ is at start of line or preceded by whitespace
        // We look at textBeforeCursor for context
        const charBeforeAt = lastAtIndex > 0 ? textBeforeCursor.charAt(lastAtIndex - 1) : '';

        if (charBeforeAt === '@') {
            this.props.handleMentionView(false, []);
            this.searchView = false;
            return;
        }

        const isValidMentionContext = lastAtIndex === 0 || /\s/.test(charBeforeAt) || !/[a-zA-Z0-9]/.test(charBeforeAt);
        if (!isValidMentionContext) return;

        // Use only text up to caret to filter (handles typing mid-text)
        const rawQuery = textBeforeCursor.substring(lastAtIndex + 1);

        // If the query starts with a space (e.g. "@ "), close the list
        if (rawQuery.length > 0 && /^\s/.test(rawQuery)) {
            this.props.handleMentionView(false, []);
            this.searchView = false;
            return;
        }

        const queryUpToCaret = rawQuery.trim();
        const queryForSearch = queryUpToCaret;

        if (queryForSearch.length === 0) {
            const groupList = this.groupMentionUsersList();
            this.props.handleMentionView(true, groupList);
            this.searchView = true;
            this.props.handleSearchView?.('');
            return;
        }

        this.searchView = true;
        this.groupMentionUsersList(queryForSearch, true);
    };

    handleMentionEnableDisable = (
        isBack,
        _lastElement, // These are based on innerHTML, which might be misleading. Use text based checks.
        _secondLastElement,
        _thirdLastElement,
        _midLastEle,
        _midSecondLastEle,
        _midPosSecondLast,
    ) => {
        // We rely on text content for logic to support long strings and ignore HTML artifacts
        const textBeforeCursor = this.getTextBeforeCursorExcludingMentions();
        const position = textBeforeCursor.length;

        // Find the last newline before cursor to get current line start
        const lastNewlineIndex = textBeforeCursor.lastIndexOf('\n');
        const currentLineStart = lastNewlineIndex === -1 ? 0 : lastNewlineIndex + 1;
        const currentLineText = textBeforeCursor.substring(currentLineStart);

        // Find @ on current line only
        const lastAtIndexOnLine = currentLineText.lastIndexOf('@');
        const lastAtIndex = lastAtIndexOnLine === -1 ? -1 : currentLineStart + lastAtIndexOnLine;

        const charBeforeAt = lastAtIndex > 0 ? textBeforeCursor.charAt(lastAtIndex - 1) : '';
        const hasValidMentionContext =
            lastAtIndex !== -1 && (lastAtIndex === 0 || /\s/.test(charBeforeAt) || !/[a-zA-Z0-9]/.test(charBeforeAt)); // Allow after emoji, punctuation, etc.

        // Check for consecutive @ symbols (@@@@) - should close mention view
        const hasConsecutiveAtSymbols = lastAtIndex > 0 && textBeforeCursor.charAt(lastAtIndex - 1) === '@';
        if (hasConsecutiveAtSymbols) {
            this.props.handleMentionView(false, []);
            this.searchView = false;
            return;
        }

        if (hasValidMentionContext) {
            const query = textBeforeCursor.substring(lastAtIndex + 1).trim();

            // Special handling for deletion (isBack)
            // When backspacing, we might be right at the '@' or inside a query
            if (isBack) {
                if (position === lastAtIndex + 1) {
                    // Just deleted to be right after '@'
                    this.props.handleMentionView(true, this.groupMentionUsersList());
                    this.searchView = true;
                    return;
                }
            }

            if (query.length === 0) {
                this.props.handleMentionView(true, this.groupMentionUsersList());
            } else {
                this.groupMentionUsersList(query, true);
            }
            this.searchView = true;
        } else {
            // Additional check: if simply typing characters that are valid context?
            // The logic here was complex with HTML elements. Simplified:
            // If we don't have a valid @ context, close it.
            this.props.handleMentionView(false, []);
            this.searchView = false;
        }
    };

    groupMentionUsersList = (filteredHtml = '', searchEnable = false) => {
        const groupMemberList = this.props.groupsMemberListData?.data?.participants;
        let grouplist = groupMemberList.filter(
            participants => participants.userId !== this.props.vCardData.data.userId,
        );
        grouplist = grouplist.map(obj => {
            obj.rosterData = getUserDetails(obj.userId);
            return obj;
        });
        if (searchEnable === true) {
            if (filteredHtml.length < 1) {
                return;
            }
            let replaceText =
                filteredHtml.includes('&amp;') === true ? filteredHtml.replace('&amp;', '&') : filteredHtml;
            filteredHtml = replaceText;
            // FR103: members match on display name or @username; "Everyone" keeps the list open.
            const searchResult = filterMentionMembers(grouplist, filteredHtml, this.props.publicUserNames);
            if (searchResult.length > 0 || isEveryoneQuery(filteredHtml)) {
                this.props.handleMentionView(true, searchResult);
            } else {
                this.props.handleMentionView(false, []);
            }
        }
        this.props.handleSearchView?.(filteredHtml);
        return grouplist;
    };

    onKeyDownListner = (e = {}) => {
        this.props.onKeyDownListner?.(e);
        const newStr = this.typingContainner.innerHTML.replace(/<br>/g, '');
        const lastElement = newStr.slice(-1);
        const secondLastElement = newStr.slice(-2, -1);
        const thirdLastElement = newStr.slice(-3, -2);
        const position = getCaretPosition(this.typingContainner);

        if (e.which === 37) {
            this.handleLeftArrowKey(secondLastElement, thirdLastElement);
        }
        if (e.which === 40 && this.props.decorate && exitBlockAtEnd(this.typingContainner)) e.preventDefault();
        if (e.which === 13 && e.shiftKey === false) {
            this.handleEnterKey(e);
        }
        // Clear mention view when creating a new line (Shift+Enter)
        if (e.which === 13 && e.shiftKey === true) {
            this.props.handleMentionView?.(false, []);
            this.searchView = false;
            this.searchValue = '';
        }
        if (e.which === 50 && e.shiftKey === true) {
            this.handle_AtSymbolKey(newStr, lastElement, secondLastElement, thirdLastElement);
        }
        if (e.which === 32) {
            this.handleSpaceBarKey(secondLastElement, thirdLastElement);
        }
        if (e.which === 8 || e.which === 46) {
            if (this.props.chatType === CHAT_TYPE_GROUP) {
                this.handleBackspaceAndDeleteKey(position, newStr, lastElement, secondLastElement, thirdLastElement);

                // Check if we should delete a mentioned user
                if (this.shouldDeleteMentionedUser()) {
                    const nodeToDelete = this.getMentionedNodeToDelete();
                    if (nodeToDelete) {
                        e.preventDefault();
                        nodeToDelete.remove();
                        this.emitChange();
                        this.props.handleDeleteMentionedUser(nodeToDelete);
                        return;
                    }
                }
            }

            if (position === this.typingContainner.innerHTML.length) {
                if (this.typingContainner.innerHTML.length <= 1) {
                    this.props.handleMessage({
                        target: {
                            value: '',
                        },
                    });
                    return;
                }
            }
        }

        if (e.ctrlKey || e.metaKey) {
            this.handleCtrlOrMetaKey(e);
        }

        return true;
    };

    // Helper method to determine if we should delete a mentioned user
    shouldDeleteMentionedUser = () => {
        const selection = window.getSelection();
        if (selection.rangeCount === 0) return false;

        const range = selection.getRangeAt(0);

        // Only delete mentioned user if cursor is at the exact boundary
        return (
            (range.startContainer.nodeType === 3 &&
                range.startOffset === 0 &&
                range.startContainer.previousSibling?.classList?.contains('mentioned')) ||
            (range.startContainer.nodeType === 1 &&
                range.startContainer.childNodes[range.startOffset - 1]?.classList?.contains('mentioned'))
        );
    };

    // Helper method to get the mentioned node to delete
    getMentionedNodeToDelete = () => {
        const selection = window.getSelection();
        if (selection.rangeCount === 0) return null;

        const range = selection.getRangeAt(0);

        if (range.startContainer.nodeType === 3 && range.startOffset === 0) {
            const nodeBeforeCaret = range.startContainer.previousSibling;
            return nodeBeforeCaret?.classList?.contains('mentioned') ? nodeBeforeCaret : null;
        }

        if (range.startContainer.nodeType === 1) {
            const nodeBeforeCaret = range.startContainer.childNodes[range.startOffset - 1];
            return nodeBeforeCaret?.classList?.contains('mentioned') ? nodeBeforeCaret : null;
        }

        return null;
    };

    handleLeftArrowKey = (secondLastElement, thirdLastElement) => {
        if (this.props.chatType === CHAT_TYPE_GROUP) {
            if (
                (secondLastElement !== '@' && thirdLastElement.length < 1) ||
                (secondLastElement !== '@' && thirdLastElement !== ' ') ||
                secondLastElement === ' ' ||
                thirdLastElement === ' '
            ) {
                this.props.handleMentionView(false, []);
            }
        }
    };

    handleEnterKey = e => {
        e.preventDefault();
        // Don't send message if mention list is open - let MentionUserList handle it
        if (this.props.showMention) {
            return false;
        }
        if (
            this.typingContainner.innerHTML.replace(/^\s+/, '') === '' ||
            this.typingContainner.innerHTML.includes('<div><br></div>')
        ) {
            return false;
        }
        if (this.props.decorate && continueBlock(this.typingContainner)) return false;
        // A refused send (E36) keeps the text in the field.
        if (this.props.handleSendTextMsg() === false) return false;
        this.props.handleEmptyContent();
        this.props.handleMentionView?.(false, []);
        // Clear search state when sending message
        this.searchView = false;
        this.searchValue = '';
        this.setState({ lastHtml: '' });
    };

    handle_AtSymbolKey = (newStr, lastElement, secondLastElement, thirdLastElement) => {
        if (this.props.chatType === CHAT_TYPE_GROUP) {
            const groupList = this.groupMentionUsersList();
            const position = getCaretPosition(this.typingContainner);
            if (position === newStr.length) {
                if (
                    lastElement === '' ||
                    secondLastElement === '@' ||
                    (secondLastElement === '@' && thirdLastElement === '@') ||
                    (lastElement === ' ' && secondLastElement === ' ') ||
                    (lastElement === ' ' && secondLastElement !== ' ')
                ) {
                    this.props.handleMentionView(true, groupList);
                    this.searchView = true;
                    this.emitChange();
                }
            } else {
                let midPos = position - 1;
                let midLastEle = newStr.charAt(position);
                let midSecondLastEle = newStr.charAt(midPos);
                let regex1 = /^ +$/;
                if (
                    (midSecondLastEle.length > 0 &&
                        (midSecondLastEle.includes(' ') === true || regex1.test(midSecondLastEle) === true) &&
                        (midLastEle.includes(' ') === true ||
                            regex1.test(midLastEle) === true ||
                            midLastEle.includes(' ') === false) &&
                        midLastEle !== '@') ||
                    ((midSecondLastEle.length < 1 || midSecondLastEle === ' ') && midLastEle === ' ')
                ) {
                    this.props.handleMentionView(true, groupList);
                    this.searchView = true;
                } else {
                    this.searchView = false;
                    this.props.handleMentionView(false, []);
                }
            }
        }
    };

    handleSpaceBarKey = (_secondLastElement, _thirdLastElement) => {
        if (this.props.chatType !== CHAT_TYPE_GROUP) return;

        const plainText =
            (this.typingContainner && (this.typingContainner.innerText || this.typingContainner.textContent)) || '';
        const pos = getCaretPosition(this.typingContainner);
        const beforeCursor = plainText.substring(0, pos);
        const lastAtIndex = beforeCursor.lastIndexOf('@');

        if (lastAtIndex !== -1) {
            // ignore an '@' that is separated from caret by a newline (previous line)
            const between = beforeCursor.substring(lastAtIndex, pos);
            if (/\r|\n/.test(between)) {
                this.props.handleMentionView(false, []);
                return;
            }
            const charBeforeAt = plainText.charAt(lastAtIndex - 1);
            const isValidMentionContext =
                lastAtIndex === 0 || /\s/.test(charBeforeAt) || !/[a-zA-Z0-9]/.test(charBeforeAt); // Allow after emoji, punctuation, etc.

            if (isValidMentionContext) {
                const afterAt = plainText.substring(lastAtIndex + 1, pos).trim();
                if (!afterAt) {
                    const groupList = this.groupMentionUsersList();
                    this.props.handleMentionView(true, groupList);
                    this.searchView = true;
                } else {
                    this.groupMentionUsersList(afterAt, true);
                    this.searchView = true;
                }
                return;
            }
        }

        // default: close mention if no @ context
        this.props.handleMentionView(false, []);
    };

    handleBackspaceAndDeleteKey = (position, newStr, lastElement, secondLastElement, thirdLastElement) => {
        let selection = window.getSelection();
        let regex = /^[a-zA-Z0-9\s!@#$%^&*(),.?":{}|<>]+$/;
        const groupList = this.groupMentionUsersList();

        if (position === newStr.length) {
            if (
                (lastElement === '@' && secondLastElement === '@') ||
                (lastElement === ' ' && secondLastElement === '@') ||
                (secondLastElement === '@' &&
                    (thirdLastElement === '@' || thirdLastElement === ' ' || thirdLastElement === '')) ||
                (thirdLastElement === '@' && regex.test(secondLastElement) === false)
            ) {
                if (selection.toString().length < 1) {
                    this.props.handleMentionView(true, groupList);
                    this.searchView = true;
                }
            } else if (
                (lastElement === '@' &&
                    lastElement.length > 0 &&
                    secondLastElement.length < 1 &&
                    thirdLastElement.length < 1) ||
                (lastElement === '@' &&
                    secondLastElement === ' ' &&
                    (thirdLastElement.length > 0 || thirdLastElement === ''))
            ) {
                this.props.handleMentionView(false, []);
            } else {
                if (selection.toString().length > 1) {
                    this.searchView = false;
                }
                this.emitChange('', true);
            }
        } else {
            const findChar = newStr.charAt(position - 1);
            if (findChar === '@') this.searchView = false;
            else this.searchView = true;
        }
    };

    handleCtrlOrMetaKey = e => {
        let charCode = String.fromCharCode(e.which).toLowerCase();
        if ((e.ctrlKey && charCode === 'v') || (e.metaKey && charCode === 'v')) {
            this.props.handleImagePaste?.();
        }
    };

    currentPosition = () => {
        const position = getCaretPosition(this.typingContainner);
        this.props.setCursorPosition?.(position);
    };

    onFocusText = () => {
        const { id = '' } = this.props;
        setTimeout(function () {
            if (document.getElementById(id)) {
                document.getElementById(id).focus();
            }
        }, 100); //delay on page render
    };

    componentDidMount() {
        this.onFocusText(); //getAutoFocus on textBox
    }

    // Update the handleInputFieldClickable method to detect clicks on mentions
    handleInputFieldClickable = e => {
        // Check if clicked element is a mentioned user
        if (e.target.classList.contains('mentioned')) {
            const userId = e.target.getAttribute('data-mentioned');

            if (this.props.setMentionedUserInfo && userId) {
                // Find the user data from group members
                const groupMemberList = this.props.groupsMemberListData?.data?.participants || [];
                const mentionedUser = groupMemberList.find(participant => participant.userId === userId);

                if (mentionedUser) {
                    const userDetails = getUserDetails(mentionedUser.userJid);
                    const mentionData = {
                        jid: mentionedUser.userJid,
                        displayName: userDetails?.nickName || mentionedUser?.name,
                        image: userDetails?.image || mentionedUser.userProfile?.image,
                        status: userDetails?.status,
                        avatarIcon: userDetails?.avatarIcon,
                        mobileNumber: userDetails?.mobileNumber,
                    };
                    this.props.setMentionedUserInfo(mentionData);
                    return;
                }
            }
        }

        // Existing click handling logic
        const lastElement = this.typingContainner.innerHTML.slice(-1);
        const secondLastElement = this.typingContainner.innerHTML.slice(-2, -1);
        let regex = /^ +$/;
        let position = getCaretPosition(this.typingContainner);

        if (this.props.chatType === CHAT_TYPE_GROUP) {
            const groupList = this.groupMentionUsersList();
            let s = window.getSelection();
            if (position === this.typingContainner.innerHTML.length) {
                if (
                    (lastElement === '@' && secondLastElement.length < 1) ||
                    (lastElement === '@' && secondLastElement === ' ') ||
                    (lastElement === '@' && regex.test(secondLastElement) === true && secondLastElement.length > 0)
                ) {
                    if (s.toString().length < 1) {
                        this.props.handleMentionView(true, groupList);
                    }
                } else if (s.toString().length < 1) {
                    this.emitChange(null, false, false, true);
                }
            }
        }
    };

    render() {
        const { placeholder = '', id = '' } = this.props;
        let { lastHtml = '' } = this.state;
        let divId = id;
        let textContent = '';
        if (lastHtml === '') {
            textContent = placeholder || i18n.t('chat.composer.PLACEHOLDER');
        }
        divId = id.includes('image-preview-typingContainer') ? 'image-preview-typingContainer' : divId;

        return (
            <div
                id={divId}
                name="typingMessage"
                contentEditable="true"
                className="typing-area"
                role="button"
                tabIndex="0"
                onInput={this.emitChange}
                onCompositionEnd={this.emitChange}
                onBeforeInput={e => refuseOverLimit(e, this.typingContainner)}
                onPaste={this.pasteAsPlainText}
                onKeyDown={e => {
                    this.onKeyDownListner(e);
                }}
                data-jest-id={'jestContentEditable'}
                data-text={textContent}
                ref={el => (this.typingContainner = el)}
                onKeyUp={() => {
                    this.currentPosition();
                }}
                onMouseUp={() => {
                    setTimeout(() => {
                        this.currentPosition();
                    }, 0);
                }}
                onFocus={e => {
                    e.target.classList.add('incaption');
                }}
                onBlur={e => {
                    e.target.classList.remove('incaption');
                    this.props.setSelectedText?.(getSelectedText());
                }}
                onClick={e => {
                    this.handleInputFieldClickable(e);
                }}
            ></div>
        );
    }
}

const mapStateToProps = (state, _props) => {
    return {
        groupsMemberListData: state.groupsMemberListData,
        vCardData: state.vCardData,
        publicUserNames: state.publicUserName.byUserId,
    };
};
const mapDispatchToProps = {
    setMentionedUserInfo,
};

export default connect(mapStateToProps, mapDispatchToProps)(ContentEditable);

ContentEditable.propTypes = {
    id: PropTypes.string,
    handleMessage: PropTypes.func,
    handleEmptyContent: PropTypes.func,
    handleMentionView: PropTypes.func,
    handleImagePaste: PropTypes.func,
    setCursorPosition: PropTypes.func,
    setSelectedText: PropTypes.func,
    onKeyDownListner: PropTypes.func,
    onInputListener: PropTypes.func,
    chatType: PropTypes.string,
    groupsMemberListData: PropTypes.oneOfType([
        PropTypes.shape({
            data: PropTypes.shape({
                participants: PropTypes.array,
            }),
        }),
        PropTypes.array,
    ]),
    vCardData: PropTypes.shape({
        data: PropTypes.shape({
            userId: PropTypes.string,
        }),
    }),
    html: PropTypes.string,
    handleSearchView: PropTypes.func,
    handleDeleteMentionedUser: PropTypes.func,
    handleSendTextMsg: PropTypes.func,
    placeholder: PropTypes.string,
    decorate: PropTypes.bool,
    publicUserNames: PropTypes.object,
    showMention: PropTypes.bool,
    handleMentionClick: PropTypes.func,
};
